import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function CallCenter() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [agentStatus, setAgentStatus] = useState('offline');
  const [callStats, setCallStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showDialModal, setShowDialModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [callForm, setCallForm] = useState({
    contact_id: '',
    phone_number: '',
    call_type: 'outgoing',
    status: 'answered',
    duration: 0,
    disposition: 'interested',
    notes: ''
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchCallStats();
    fetchRecentCalls();
    fetchContacts();
  }, []);

  const fetchCallStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/call-center/calls/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCallStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRecentCalls = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/call-center/calls/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentCalls(response.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/crm/contacts?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/call-center/agents/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgentStatus(newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const handleLogCall = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/call-center/calls/log`,
        callForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowLogModal(false);
      setCallForm({
        contact_id: '',
        phone_number: '',
        call_type: 'outgoing',
        status: 'answered',
        duration: 0,
        disposition: 'interested',
        notes: ''
      });
      fetchCallStats();
      fetchRecentCalls();
      alert('Call logged successfully!');
    } catch (error) {
      console.error('Failed to log call:', error);
      alert(error.response?.data?.detail || 'Failed to log call');
    }
  };

  const handleDialContact = (contact) => {
    setSelectedContact(contact);
    setCallForm({
      ...callForm,
      contact_id: contact.id,
      phone_number: contact.phone
    });
    setShowDialModal(true);
  };

  const handleInitiateCall = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/call-center/calls/initiate`,
        {
          contact_id: selectedContact.id,
          phone_number: selectedContact.phone,
          from_number: '1800-XXX-XXXX' // Replace with your number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDialModal(false);
      alert('Call initiated! Now calling: ' + selectedContact.phone);
      // After call ends, show disposition modal
      setTimeout(() => {
        setShowLogModal(true);
        setCallForm({
          ...callForm,
          contact_id: selectedContact.id,
          phone_number: selectedContact.phone,
          call_type: 'outgoing'
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to initiate call:', error);
      alert('Failed to initiate call. You can manually dial and log the call.');
      setShowDialModal(false);
      setShowLogModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-500',
      busy: 'bg-red-500',
      break: 'bg-yellow-500',
      offline: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getCallStatusBadge = (status) => {
    const colors = {
      answered: 'bg-green-100 text-green-800',
      missed: 'bg-red-100 text-red-800',
      rejected: 'bg-orange-100 text-orange-800',
      busy: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#2b3380' }}>Call Center</h1>
          <p className="text-gray-600">Manage customer calls and track conversations</p>
        </div>

        {/* Agent Status Bar */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(agentStatus)}`}></div>
                <span className="font-medium">Status: {agentStatus.toUpperCase()}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleStatusChange('available')}
                className={`px-4 py-2 rounded-lg ${agentStatus === 'available' ? 'bg-green-500 text-white' : 'bg-gray-100'}`}
              >
                ✓ Available
              </button>
              <button
                onClick={() => handleStatusChange('busy')}
                className={`px-4 py-2 rounded-lg ${agentStatus === 'busy' ? 'bg-red-500 text-white' : 'bg-gray-100'}`}
              >
                📞 Busy
              </button>
              <button
                onClick={() => handleStatusChange('break')}
                className={`px-4 py-2 rounded-lg ${agentStatus === 'break' ? 'bg-yellow-500 text-white' : 'bg-gray-100'}`}
              >
                ☕ Break
              </button>
              <button
                onClick={() => handleStatusChange('offline')}
                className={`px-4 py-2 rounded-lg ${agentStatus === 'offline' ? 'bg-gray-500 text-white' : 'bg-gray-100'}`}
              >
                🔴 Offline
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {callStats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Total Calls</div>
              <div className="text-2xl font-bold" style={{ color: '#2b3380' }}>{callStats.total_calls}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Incoming</div>
              <div className="text-2xl font-bold text-blue-600">{callStats.incoming_calls}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Outgoing</div>
              <div className="text-2xl font-bold text-green-600">{callStats.outgoing_calls}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Answered</div>
              <div className="text-2xl font-bold text-green-600">{callStats.answered_calls}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Avg Duration</div>
              <div className="text-2xl font-bold text-purple-600">{formatDuration(Math.round(callStats.average_duration_seconds))}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex gap-4">
            <button
              onClick={() => setShowLogModal(true)}
              className="px-6 py-3 text-white rounded-lg hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: '#2b3380' }}
            >
              📝 Log Call
            </button>
            <button
              onClick={() => navigate('/crm')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:opacity-90 flex items-center gap-2"
            >
              📞 Call Contact from CRM
            </button>
          </div>
        </div>

        {/* Recent Calls */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#2b3380' }}>Recent Calls</h2>
          {recentCalls.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-5xl mb-4">📞</div>
              <div className="text-lg font-medium mb-2">No calls logged yet</div>
              <div className="text-sm">Click "Log Call" to record your first call</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Contact</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Phone</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Duration</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Disposition</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentCalls.map(call => (
                    <tr key={call.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{call.agent_name}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{call.phone_number}</td>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-medium ${
                          call.call_type === 'incoming' ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          {call.call_type === 'incoming' ? '📥 Incoming' : '📤 Outgoing'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCallStatusBadge(call.status)}`}>
                          {call.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{formatDuration(call.duration)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{call.disposition || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(call.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {call.notes ? (
                          <span className="truncate max-w-xs block" title={call.notes}>
                            {call.notes.substring(0, 50)}{call.notes.length > 50 ? '...' : ''}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Log Call Modal */}
        {showLogModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{ color: '#2b3380' }}>Log Call</h2>
                  <button
                    onClick={() => setShowLogModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleLogCall}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact *</label>
                      <select
                        value={callForm.contact_id}
                        onChange={(e) => {
                          const contact = contacts.find(c => c.id === e.target.value);
                          setCallForm({
                            ...callForm,
                            contact_id: e.target.value,
                            phone_number: contact?.phone || ''
                          });
                        }}
                        className="w-full px-4 py-2 border rounded-lg"
                        required
                      >
                        <option value="">Select Contact</option>
                        {contacts.map(contact => (
                          <option key={contact.id} value={contact.id}>
                            {contact.first_name} {contact.last_name} - {contact.phone}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        value={callForm.phone_number}
                        onChange={(e) => setCallForm({ ...callForm, phone_number: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Call Type *</label>
                      <select
                        value={callForm.call_type}
                        onChange={(e) => setCallForm({ ...callForm, call_type: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="incoming">Incoming</option>
                        <option value="outgoing">Outgoing</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Call Status *</label>
                      <select
                        value={callForm.status}
                        onChange={(e) => setCallForm({ ...callForm, status: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="answered">Answered</option>
                        <option value="missed">Missed</option>
                        <option value="rejected">Rejected</option>
                        <option value="busy">Busy</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Duration (seconds)</label>
                      <input
                        type="number"
                        value={callForm.duration}
                        onChange={(e) => setCallForm({ ...callForm, duration: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border rounded-lg"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Disposition</label>
                      <select
                        value={callForm.disposition}
                        onChange={(e) => setCallForm({ ...callForm, disposition: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="interested">Interested</option>
                        <option value="not_interested">Not Interested</option>
                        <option value="callback">Callback Required</option>
                        <option value="no_answer">No Answer</option>
                        <option value="converted">Converted</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                      <textarea
                        value={callForm.notes}
                        onChange={(e) => setCallForm({ ...callForm, notes: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg"
                        rows="3"
                        placeholder="Add call notes, next steps, etc."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={() => setShowLogModal(false)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 text-white rounded-lg hover:opacity-90"
                      style={{ backgroundColor: '#2b3380' }}
                    >
                      Log Call
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Dial Contact Modal */}
        {showDialModal && selectedContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4" style={{ color: '#2b3380' }}>Call Contact</h2>
                <div className="mb-6">
                  <div className="text-lg font-medium">{selectedContact.first_name} {selectedContact.last_name}</div>
                  <div className="text-gray-600">{selectedContact.phone}</div>
                  {selectedContact.company_name && (
                    <div className="text-sm text-gray-500">{selectedContact.company_name}</div>
                  )}
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={() => setShowDialModal(false)}
                    className="flex-1 px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInitiateCall}
                    className="flex-1 px-6 py-2 bg-green-600 text-white rounded-lg hover:opacity-90"
                  >
                    📞 Call Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CallCenter;
